/**
 * エージェント API の契約（`/api/v1/agent/*`）。
 * **ARCHITECTURE.md §4.3 / §7.1 が唯一の正**。
 *
 * 認証は **Ed25519 private_key_jwt → 15 分アクセストークン**:
 *
 *   1. `POST /agent/enroll` … 使い捨ての enroll トークンを提示し、ノード側で
 *      生成した Ed25519 **公開鍵**を登録する。応答で nodeId と CP 公開鍵を得る。
 *   2. `POST /agent/token` … `client_assertion`（自ノードの秘密鍵で署名した JWT）
 *      を提示して 15 分のアクセストークンを得る。
 *   3. 以降は `Authorization: Bearer <access token>`。
 *
 * CP は**公開鍵しか持たない**。旧版の HMAC リクエスト署名（`X-MailSV-*` 5 ヘッダ）
 * と `agent_token_hash` は廃止した。CP 側 DB が漏れても全ノードのシークレットが
 * 復号可能、という最大のリスクが原理的に消えるため。
 *
 * CP → エージェント方向は、desired-state / secrets / リリース記述子に付いた
 * **CP の Ed25519 署名**を、enroll 時にピン留めした公開鍵で検証する。
 * 検証失敗は適用せず**フェイルフリーズ**（直前の last-good を維持）。
 */
import { z } from 'zod';
import { nodeApplyStateSchema, nodeHealthSchema } from '../enums.js';
import type {
  DESIRED_STATE_SECTIONS} from './desired-state.js';
import {
  desiredStateSectionSchema,
  sha256HexSchema,
  signedDesiredStateSchema,
} from './desired-state.js';
import { nodeCommandResultSchema, nodeCommandSchema } from './node-command.js';

/* ------------------------------------------------------------ パス定数 */

export const AGENT_API_PREFIX = '/api/v1/agent';

export const AGENT_ROUTES = {
  enroll: `${AGENT_API_PREFIX}/enroll`,
  token: `${AGENT_API_PREFIX}/token`,
  state: `${AGENT_API_PREFIX}/state`,
  ack: `${AGENT_API_PREFIX}/ack`,
  status: `${AGENT_API_PREFIX}/status`,
  metrics: `${AGENT_API_PREFIX}/metrics`,
  events: `${AGENT_API_PREFIX}/events`,
  usage: `${AGENT_API_PREFIX}/usage`,
  /**
   * 配送イベント（送信・バウンス・拒否）の報告。R0 §2.3 で追加。
   * CP はこれを受けて `send_rate_counters` / `org_send_rate_counters` を積み、
   * DSN を分類して `suppressions` へ自動投入する。
   */
  deliveryEvents: `${AGENT_API_PREFIX}/delivery-events`,
  /**
   * IMAP / SMTP / Submission の**認証イベント**の報告（R134）。
   *
   * 要件は「ポータル / IMAP / SMTP の認証ログを 1 年保持し、契約者が
   * 自分の分を見られる」と書いているが、CP に来ていたのは**ポータルの
   * 認証だけ**だった。IMAP / SMTP の認証はノードローカルの
   * `mailnode.auth_failures` に入るものの、**失敗しか記録せず**CP へも
   * 上がらない。契約者が「乗っ取られたかもしれない」と申告してきたときに
   * **成功したログインを誰も見られない**（いちばん見たいものが無い）。
   */
  authEvents: `${AGENT_API_PREFIX}/auth-events`,
  /**
   * FBL（苦情フィードバックループ / ARF）の報告。A283 / A303 で追加。
   * **苦情の一次情報は ISP が返す ARF だけ**で、受け口が無いと
   * 苦情率は永久に 0% を指し、サーキットブレーカが一度も発火しない。
   */
  feedbackReports: `${AGENT_API_PREFIX}/feedback-reports`,
  /**
   * 一斉配信のバッチをリースする（CMS_DESIGN §5.6 の LEASE）。
   * **ノードから CP へ取りに行く**（CP からノードへ押し込まない）。
   * 押し込む形にすると、ノードが落ちている間に積まれた分の行方が
   * CP 側の再送キューに依存し、二重注入の窓が開く。
   */
  bulkLease: `${AGENT_API_PREFIX}/bulk/lease`,
  /** リースしたバッチの注入結果を返す。 */
  bulkReport: `${AGENT_API_PREFIX}/bulk/report`,
  /** 抑止リスト（差分）の取得。ノード側の送信経路で強制するために引く。 */
  suppressions: `${AGENT_API_PREFIX}/suppressions`,
  /**
   * ノード上のサービスが検証に使う資格情報（Dovecot マスターユーザー）。
   * **ハッシュだけ**を返す口で、`secrets`（平文）とは別系統（A106）。
   */
  serviceCredentials: `${AGENT_API_PREFIX}/service-credentials`,
  audit: `${AGENT_API_PREFIX}/audit`,
  backupStatus: `${AGENT_API_PREFIX}/backup-status`,
  releaseReport: `${AGENT_API_PREFIX}/release/report`,
  commandsPoll: `${AGENT_API_PREFIX}/commands/poll`,
  acmeDnsChallenge: `${AGENT_API_PREFIX}/acme/dns-challenge`,
} as const;

export function agentStateSectionPath(section: (typeof DESIRED_STATE_SECTIONS)[number]): string {
  return `${AGENT_API_PREFIX}/state/${section}`;
}

export function agentSecretPath(kind: AgentSecretKind, id: string): string {
  return `${AGENT_API_PREFIX}/secrets/${kind}/${encodeURIComponent(id)}`;
}

export function agentCommandResultPath(commandId: string): string {
  return `${AGENT_API_PREFIX}/commands/${encodeURIComponent(commandId)}/result`;
}

/**
 * 配信物（ビルド済み HTML / テキスト）の取得。
 *
 * **リース応答に本文を載せない**のは、同じ成果物を全バッチで
 * 何度も転送しないため。10 万通を 500 件ずつに割ると 200 バッチになり、
 * 本文 200KB なら 40MB を無駄に往復することになる。ノードは
 * `artifactSha256` をキーに 1 回だけ取って使い回す。
 */
export function agentBulkArtifactPath(artifactId: string): string {
  return `${AGENT_API_PREFIX}/bulk/artifacts/${encodeURIComponent(artifactId)}`;
}

/* ------------------------------------------------------------ 定数・ヘッダ */

/** アクセストークンの有効期間（env `AGENT_TOKEN_TTL_SEC` の既定）。 */
export const AGENT_TOKEN_TTL_SEC = 900;
/** private_key_jwt の最大寿命。`exp = iat + 60`。 */
export const AGENT_ASSERTION_MAX_LIFETIME_SEC = 60;
/** `jti` を Redis に SETNX しておく秒数（リプレイ防止）。 */
export const AGENT_JTI_TTL_SEC = 120;
/** 許容する時計ずれ。 */
export const AGENT_CLOCK_SKEW_SEC = 60;
/** `?wait=` の上限（ミリ秒）。 */
export const AGENT_LONGPOLL_MAX_MS = 25_000;
/** long-poll の既定待機秒数。 */
export const AGENT_LONGPOLL_DEFAULT_SEC = 25;

/**
 * `payloadHash` の原像から**除外する**キー。
 *
 *   payloadHash = sha256(canonicalJson(state から ここに挙げたキーを除いたもの))
 *
 * **CP（`state-publish`）とエージェント（`cp/verify.ts`）はこの表を共有する。**
 * ここを 2 か所に分けて書いていたせいで実際に起きたこと（2026-08-23 に実機で
 * 発覚）:
 *
 *   * CP 側 … `generation` / `issuedAt` / `staleAfter` / `payloadHash` を除外
 *   * エージェント側 … `payloadHash` **だけ**を除外
 *
 * つまり**どの世代も検証を通らず**、エージェントは受け取った desired-state を
 * 1 度も適用できない（毎回フェイルフリーズ）。しかも CP からは
 * 「ノードは生きていて pull もしている」ようにしか見えないので、
 * ログを読むまで気づけない。
 *
 * 時刻と世代を除くのは「中身が同じなら hash も同じ」を成立させるため。
 * 含めると毎回ハッシュが変わり、`payloadHash` が同じなら世代を上げない、
 * という publish 側の規則が意味を失う（世代だけが際限なく進む）。
 *
 * `nodeId` は**除外しない**。除外すると、あるノード宛の署名済み payload を
 * 別ノードへ流用できてしまう。
 */
export const DESIRED_STATE_HASH_EXCLUDED_KEYS = [
  'generation',
  'issuedAt',
  'staleAfter',
  'payloadHash',
] as const;
export type DesiredStateHashExcludedKey = (typeof DESIRED_STATE_HASH_EXCLUDED_KEYS)[number];

/**
 * `payloadHash` の原像（正規化 JSON 文字列）を作る。
 *
 * ハッシュ関数そのものはここに置かない。`@mail-sv/shared` は SPA からも
 * import されるので `node:crypto` を持ち込めない（バンドルが壊れる）。
 * **原像の作り方だけ**を共有し、sha256 は各側で掛ける。
 */
export function desiredStatePayloadPreimage(
  state: Record<string, unknown>,
  canonicalJson: (value: unknown) => string,
): string {
  const rest: Record<string, unknown> = {};
  const excluded = new Set<string>(DESIRED_STATE_HASH_EXCLUDED_KEYS);
  for (const [k, v] of Object.entries(state)) {
    if (!excluded.has(k)) rest[k] = v;
  }
  return canonicalJson(rest);
}

/* -------------------------------------------- CP 署名鍵の ID（唯一の導出） */

/**
 * CP 署名鍵の ID を公開鍵から決定的に導く。**導出はここ 1 か所だけ。**
 *
 * ## なぜ共有に引き上げたか（2026-09-05 に VPS で踏んだ）
 *
 * 同じ鍵に対して 2 か所が**違う ID** を作っていた:
 *
 *   apps/api/src/services/signing.ts   `cp-${sha256Hex(publicKeyBase64).slice(0,12)}`
 *   apps/worker/src/lib/signing.ts     `cpk_${sha256Hex(rawBytes).slice(0,16)}`
 *
 * 接頭辞・長さ・**ハッシュの入力**（base64 文字列か生バイトか）の 3 つが違う。
 * エージェントは enroll 時に **API** から鍵を受け取ってピン留めし、
 * desired-state は **worker** が署名して `signing_key_id` を載せる。
 * 照合は `pinned.find(k => k.keyId === signingKeyId)` の**完全一致**なので、
 *
 *   ピン留め: cp-82d20861c2e0   /   受領: cpk_69c7babd0021ce5e
 *
 * となり、**署名は正しいのに検証が必ず落ちる**。ノードは 1 世代も
 * desired-state を適用できない。しかも heartbeat は正常に届くので、
 * CP からはノードが健全に見え、ログを読むまで気づけない
 * （`desiredStatePayloadPreimage` の docstring が書いている事故と同じ形が、
 *  ハッシュの原像ではなく鍵 ID で再発した）。
 *
 * ## 形式を `cpk_` + 生バイトの sha256 に寄せた理由
 *
 * - **生バイトを hash する。** base64 文字列を hash すると、同じ鍵でも
 *   パディングや改行の有無で ID が変わりうる。鍵の同一性は
 *   エンコード表現ではなく鍵そのもので決めるべき。
 * - **既に永続化されている形式がこちら。** `node_desired_state.signing_key_id`
 *   に入っている値は worker が書いた `cpk_` 形式で、API 側を寄せれば
 *   既存行はそのまま有効なまま使える（逆向きに寄せると全行が孤児になる）。
 *
 * ハッシュ関数を引数で受けるのは `desiredStatePayloadPreimage` と同じ理由で、
 * `@mail-sv/shared` は SPA からも import されるため `node:crypto` を
 * 持ち込めないから。
 *
 * @param rawPublicKey Ed25519 の**生 32 バイト**公開鍵（SPKI ではない）
 */
export function controlPlaneSigningKeyId<T extends Uint8Array>(
  rawPublicKey: T,
  sha256Hex: (value: T) => string,
): string {
  return `cpk_${sha256Hex(rawPublicKey).slice(0, 16)}`;
}

/**
 * 認証は Bearer のみ。旧 `x-mailsv-signature` / `x-mailsv-timestamp` /
 * `x-mailsv-node` は**削除**（private_key_jwt に一本化したため不要）。
 */
export const AGENT_HEADERS = {
  /** 運用画面での版数表示と互換性判定にだけ使う。認証には使わない。 */
  agentVersion: 'x-mailsv-agent-version',
  /** POST 系の重複排除（usage / events / audit で必須）。 */
  idempotencyKey: 'idempotency-key',
} as const;

/** desired-state の ETag。`"gen-<generation>"` 形式。 */
export function generationEtag(generation: number): string {
  return `"gen-${generation}"`;
}

export function parseGenerationEtag(etag: string | null | undefined): number | null {
  if (!etag) return null;
  const m = /^(?:W\/)?"gen-(\d+)"$/.exec(etag.trim());
  return m ? Number(m[1]) : null;
}

/* ---------------------------------------------------------------- 登録 */

export const agentEnrollRequestSchema = z.object({
  /** `node_enrollment_tokens` に発行された使い捨てトークン（生値）。 */
  enrollmentToken: z.string().min(16),
  hostname: z.string().min(1),
  publicIpv4: z.array(z.string().ip({ version: 'v4' })).default([]),
  /**
   * **「送信に使う IPv6」だけを入れる。**「ノードが持っている v6」ではない。
   *
   * この配列の読み手は全員これを出口 IP として扱う:
   *   - サービス SPF の `ip6:`（`buildServiceSpfRecord()`）
   *   - FCrDNS / PTR 検査の対象（`verifyNodeDns()`）
   * 送信に使わない v6（ULA / ブリッジ割当 / 受信専用）を入れると、
   * **直しようのない critical が IP ごと・日ごとに永久に出続ける**。
   * エージェントは `MAILSV_NODE_IPV6_MODE=full` のときだけ値を入れる。
   */
  publicIpv6: z.array(z.string().ip({ version: 'v6' })).default([]),
  agentVersion: z.string().min(1),
  /**
   * ノードが生成した Ed25519 **公開鍵**（JWK, `kty=OKP` / `crv=Ed25519`）。
   * 秘密鍵はノードから出さない。CP は公開鍵しか持たない。
   */
  publicKeyJwk: z.object({
    kty: z.literal('OKP'),
    crv: z.literal('Ed25519'),
    x: z.string().min(1),
    kid: z.string().min(1).optional(),
  }),
  /** OS / カーネル / CPU / メモリなど。運用画面の表示用。 */
  systemInfo: z.record(z.string(), z.unknown()).default({}),
});
export type AgentEnrollRequest = z.infer<typeof agentEnrollRequestSchema>;

export const agentEnrollResponseSchema = z.object({
  nodeId: z.string().min(1),
  /**
   * CP の Ed25519 公開鍵（base64url raw 32 バイト）。
   * エージェントはこれを**ピン留め**し、以後 desired-state / リリース記述子の
   * 署名検証に使う。ローテーション対応のため 2 本まで許容する。
   */
  controlPlanePublicKeys: z
    .array(z.object({ keyId: z.string().min(1), publicKey: z.string().min(1) }))
    .min(1)
    .max(2),
  /** トークン取得時に `aud` として使う絶対 URL。 */
  tokenEndpoint: z.string().url(),
  /** 初回の desired-state 世代。0 なら未 publish。 */
  generation: z.number().int().nonnegative(),
});
export type AgentEnrollResponse = z.infer<typeof agentEnrollResponseSchema>;

/* ------------------------------------------------------ トークン取得 */

/** private_key_jwt のクレーム。`iss` と `sub` はどちらも nodeId。 */
export const agentAssertionClaimsSchema = z.object({
  iss: z.string().min(1),
  sub: z.string().min(1),
  /** `<PUBLIC_URL>/api/v1/agent/token`。他のエンドポイントへ転用させない。 */
  aud: z.string().url(),
  iat: z.number().int(),
  exp: z.number().int(),
  /** リプレイ防止。Redis `agent:jti:{nodeId}:{jti}` に 120 秒 SETNX。 */
  jti: z.string().min(8),
});
export type AgentAssertionClaims = z.infer<typeof agentAssertionClaimsSchema>;

/** RFC 7523 に倣った形。`grant_type` は固定。 */
export const agentTokenRequestSchema = z.object({
  grant_type: z.literal('client_credentials'),
  client_assertion_type: z.literal('urn:ietf:params:oauth:client-assertion-type:jwt-bearer'),
  /** ヘッダ `alg=EdDSA` の JWS Compact。 */
  client_assertion: z.string().min(1),
});
export type AgentTokenRequest = z.infer<typeof agentTokenRequestSchema>;

export const agentTokenResponseSchema = z.object({
  access_token: z.string().min(1),
  token_type: z.literal('Bearer'),
  expires_in: z.number().int().positive(),
});
export type AgentTokenResponse = z.infer<typeof agentTokenResponseSchema>;

/* --------------------------------------------------- desired-state 取得 */

export const agentStateQuerySchema = z.object({
  /** long-poll の待機秒数。0..25。省略時は即時応答。 */
  wait: z.coerce.number().int().min(0).max(AGENT_LONGPOLL_DEFAULT_SEC).optional(),
});
export type AgentStateQuery = z.infer<typeof agentStateQuerySchema>;

/**
 * `GET /agent/state` の応答（200 のとき）。
 * `If-None-Match: "gen-N"` が現在世代と一致すれば **304** を返し本文は無い。
 */
export const agentStateResponseSchema = signedDesiredStateSchema;
export type AgentStateResponse = z.infer<typeof agentStateResponseSchema>;

export const agentStateSectionQuerySchema = z.object({
  /** 本体の `collections[].digest`。ズレていれば 409（世代が進んでいる）。 */
  digest: sha256HexSchema.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(2000).default(1000),
});
export type AgentStateSectionQuery = z.infer<typeof agentStateSectionQuerySchema>;

export const agentStateSectionResponseSchema = z.object({
  section: desiredStateSectionSchema,
  generation: z.number().int().nonnegative(),
  digest: sha256HexSchema,
  /** 項目の型はセクションごとに異なる（`DESIRED_STATE_SECTION_SCHEMAS` で検証）。 */
  items: z.array(z.unknown()),
  nextCursor: z.string().nullable(),
});
export type AgentStateSectionResponse = z.infer<typeof agentStateSectionResponseSchema>;

/* -------------------------------------------------------------- 秘密取得 */

/**
 * `restic` / `backup_s3` を足したのは R0 §2.4 のため。
 * restic のリポジトリパスフレーズがノード上のファイルにしか無いと、
 * **ノードが全損した瞬間に S3 のバックアップが復号不能なゴミになる**。
 * 正本を CP の `node_secrets` に置き、ノードへは配布するだけにする。
 */
export const AGENT_SECRET_KIND = ['dkim', 'cert', 'srs', 'restic', 'backup_s3'] as const;
export type AgentSecretKind = (typeof AGENT_SECRET_KIND)[number];
export const agentSecretKindSchema = z.enum(AGENT_SECRET_KIND);

export const agentSecretQuerySchema = z.object({
  version: z.coerce.number().int().positive(),
});
export type AgentSecretQuery = z.infer<typeof agentSecretQuerySchema>;

/**
 * 秘密の実体。応答は**キャッシュ可**（エージェントがディスクに保存し、
 * CP 停止中も last-good で動く）。CP は取得のたびに `secret.viewed` を監査する。
 */
export const agentSecretResponseSchema = z.object({
  kind: agentSecretKindSchema,
  id: z.string().min(1),
  version: z.number().int().positive(),
  /** PEM もしくは生の秘密。用途は kind で決まる。 */
  material: z.string().min(1),
  /** 証明書の場合のみ。 */
  chainPem: z.string().nullable().default(null),
  /** `sha256(material)`。書き込み後の検証に使う。 */
  materialHash: sha256HexSchema,
  notAfter: z.string().datetime().nullable().default(null),
});
export type AgentSecretResponse = z.infer<typeof agentSecretResponseSchema>;

/* -------------------------------------------------- サービス資格情報 */

/**
 * ノード上のサービスが検証に使う資格情報（§7.6）。
 *
 * 今は Dovecot のマスターユーザーだけ。Webメール（aurora-mail）は
 * `user@example.jp*aurora` の形で IMAP に入るので、**ノードの
 * `mailnode.service_credentials` にマスターユーザーのハッシュが 1 行無いと
 * Webメールは 1 通も読めない**。
 *
 * ここを `secrets` の系統と分けたのは 2 つの理由による。
 *   1. 配るのは**ハッシュだけ**で、平文はノードへ渡さない。`GET /agent/secrets`
 *      は平文を返す口なので、混ぜると「ノードが平文を持てる」設計に見える。
 *   2. desired-state の `secrets` に載せる形にすると、ローテーションのたびに
 *      世代を発行し直す必要がある。マスター資格情報は 90 日周期で回るだけで
 *      他のどのセクションとも独立に動くので、別の口にして毎周期取りに行く。
 */
export const AGENT_SERVICE_CREDENTIAL_KIND = ['dovecot_master'] as const;
export type AgentServiceCredentialKind = (typeof AGENT_SERVICE_CREDENTIAL_KIND)[number];
export const agentServiceCredentialKindSchema = z.enum(AGENT_SERVICE_CREDENTIAL_KIND);

export const agentServiceCredentialSchema = z.object({
  kind: agentServiceCredentialKindSchema,
  username: z.string().min(1).max(64),
  /**
   * Dovecot がそのまま検証できる形（`{ARGON2ID}$argon2id$…`）。
   * **平文は絶対に載せない。**
   */
  passwordHash: z.string().min(1).max(512),
  version: z.number().int().positive(),
});
export type AgentServiceCredential = z.infer<typeof agentServiceCredentialSchema>;

export const agentServiceCredentialsResponseSchema = z.object({
  credentials: z.array(agentServiceCredentialSchema),
  /**
   * 内容の sha256。前回と同じならエージェントは DB を触らない
   * （毎周期 UPDATE すると `updated_at` だけが動いて監査の意味が薄れる）。
   */
  digest: sha256HexSchema,
});
export type AgentServiceCredentialsResponse = z.infer<typeof agentServiceCredentialsResponseSchema>;

/* ---------------------------------------------------------------- ack */

/** 世代の適用結果。これを受けて CP は `node_reported_state` を更新する。 */
export const agentAckRequestSchema = z.object({
  generation: z.number().int().nonnegative(),
  payloadHash: sha256HexSchema,
  applyState: nodeApplyStateSchema,
  /** 失敗時のみ。秘密情報を含めてはならない。 */
  applyError: z.string().max(4000).nullable().default(null),
  appliedAt: z.string().datetime().nullable().default(null),
  durationMs: z.number().int().nonnegative().default(0),
  /** 適用したセクションの digest。CP 側で drift 判定に使う。 */
  sectionDigests: z.record(z.string(), sha256HexSchema).default({}),
});
export type AgentAckRequest = z.infer<typeof agentAckRequestSchema>;

/* -------------------------------------------------------------- status */

export const componentStatusSchema = z.object({
  running: z.boolean(),
  version: z.string().nullable(),
  /** 生成した設定ファイル群の sha256。drift 検知に使う。 */
  configHash: z.string().nullable(),
  lastError: z.string().nullable(),
});
export type ComponentStatus = z.infer<typeof componentStatusSchema>;

export const driftItemSchema = z.object({
  /** `postfix:main.cf` / `mailnode.mailboxes:user@example.jp` のような識別子。 */
  target: z.string().min(1),
  kind: z.enum(['missing', 'unexpected', 'modified']),
  detail: z.string(),
  detectedAt: z.string().datetime(),
});
export type DriftItem = z.infer<typeof driftItemSchema>;

/** 30 秒ごと（`MAILSV_STATUS_INTERVAL_SEC`）。ハートビートを兼ねる。 */
export const agentStatusRequestSchema = z.object({
  agentVersion: z.string().min(1),
  releaseVersion: z.string().nullable().default(null),
  appliedGeneration: z.number().int().nonnegative(),
  applyingGeneration: z.number().int().nonnegative().nullable().default(null),
  health: nodeHealthSchema,
  components: z.record(z.string(), componentStatusSchema).default({}),
  drift: z.array(driftItemSchema).default([]),
  /**
   * 自ノード IP の RBL 掲載状況（`MAILSV_RBL_ZONES`）。
   *
   * **`responseAddresses` を必ず載せること。** DNSBL は「掲載」も「クエリ拒否」も
   * A レコードで返すので、応答があったこと自体では区別できない。
   * Spamhaus の無償ゾーンを公開リゾルバ経由で叩くと 127.255.255.252 が返り、
   * 値を見ずに掲載扱いすると**全ノードが常時 critical**になる。
   * CP 側は `classifyRblAnswer()`（`@mail-sv/shared`）で再判定し、
   * `responseAddresses` が空の報告は「検証不能」として掲載扱いしない。
   */
  rblListings: z
    .array(
      z.object({
        zone: z.string().min(1),
        ip: z.string().min(1),
        /** そのゾーンへの問い合わせで返った A レコードの値。 */
        responseAddresses: z.array(z.string()).default([]),
        /** 掲載理由の TXT（あれば）。解除申請の判断材料になる。 */
        txt: z.string().max(1000).nullable().default(null),
        checkedAt: z.string().datetime().nullable().default(null),
      }),
    )
    .default([]),
  /**
   * ノードのディスク上に**実在する**証明書の期限（R0 §2.4）。
   *
   * CP の `tls_certificates` は ACME 発行の台帳で、ノードのファイルではない。
   * 手で置いた証明書は台帳に行が無く、配布に失敗したノードは古い PEM のまま
   * 動く。どちらも台帳だけ見ていると「更新できている」ように見えたまま、
   * ある日 IMAPS(993) / Submission(587) の TLS が全滅する。
   *
   * **毎回すべての証明書を載せる（差分ではない）。** CP は報告に含まれない
   * name をその場で消す。消えた証明書について通知が鳴り続けないようにするため。
   */
  certExpiries: z
    .array(
      z.object({
        /** `current/tls/<name>/fullchain.pem` の `<name>`。 */
        name: z.string().min(1).max(253),
        /** 証明書の中身から読んだ subject（ファイル名ではなく中身が正）。 */
        subject: z.string().max(512).nullable().default(null),
        notBefore: z.string().datetime().nullable().default(null),
        notAfter: z.string().datetime(),
        /** 台帳との目視突合用。一致しないこと自体は異常ではない。 */
        fingerprintSha256: z.string().max(128).nullable().default(null),
      }),
    )
    .max(200)
    .default([]),
  /** 特権ヘルパの疎通。落ちていると reload が届かない。 */
  helpers: z
    .object({
      shim: z.boolean(),
      netguard: z.boolean(),
      updater: z.boolean(),
    })
    .partial()
    .default({}),
});
export type AgentStatusRequest = z.infer<typeof agentStatusRequestSchema>;

export const agentStatusResponseSchema = z.object({
  /** 最新 generation。適用済みと違えばエージェントは即 pull し直す。 */
  latestGeneration: z.number().int().nonnegative(),
  /** 待機中のコマンドがあるか。あれば `commands/poll` を叩く。 */
  commandsPending: z.boolean(),
  /** サーバー側から間隔を動的に変えられるようにしておく（輻輳時の緩和）。 */
  statusIntervalSec: z.number().int().positive(),
  metricsIntervalSec: z.number().int().positive(),
  /** CP 側の時刻。エージェントは時計ずれの検知にだけ使う（合わせには使わない）。 */
  serverTime: z.string().datetime(),
});
export type AgentStatusResponse = z.infer<typeof agentStatusResponseSchema>;

/* ------------------------------------------------------------- metrics */

/** 60 秒ごと（`MAILSV_METRICS_INTERVAL_SEC`）。時系列の数値だけを送る。 */
export const agentMetricsRequestSchema = z.object({
  collectedAt: z.string().datetime(),
  cpuLoad1: z.number().nullable().default(null),
  cpuLoad5: z.number().nullable().default(null),
  memUsedBytes: z.number().int().nonnegative().nullable().default(null),
  memTotalBytes: z.number().int().nonnegative().nullable().default(null),
  diskUsedBytes: z.number().int().nonnegative().nullable().default(null),
  diskTotalBytes: z.number().int().nonnegative().nullable().default(null),
  /** Postfix キュー（shim の `queuestats`）。 */
  queueActive: z.number().int().nonnegative().nullable().default(null),
  queueDeferred: z.number().int().nonnegative().nullable().default(null),
  queueHold: z.number().int().nonnegative().nullable().default(null),
  smtpConnCurrent: z.number().int().nonnegative().nullable().default(null),
  imapConnCurrent: z.number().int().nonnegative().nullable().default(null),
  inboundLastMin: z.number().int().nonnegative().nullable().default(null),
  outboundLastMin: z.number().int().nonnegative().nullable().default(null),
  rejectedLastMin: z.number().int().nonnegative().nullable().default(null),
  greylistedLastMin: z.number().int().nonnegative().nullable().default(null),
  clamavSignatureDate: z.string().nullable().default(null),
});
export type AgentMetricsRequest = z.infer<typeof agentMetricsRequestSchema>;

/* -------------------------------------------------------------- events */

export const AGENT_EVENT_KIND = [
  'auth_failure_burst',
  'send_rate_exceeded',
  'spam_outbound',
  'relay_attempt',
  'sender_mismatch',
  'tls_handshake_failure',
  'ip_banned',
  'ip_unbanned',
  'queue_backlog',
  'disk_pressure',
  /**
   * **CP 公開鍵で検証できない desired-state を掴まされた**（R102）。
   *
   * エージェントはこれを受けると適用せず last-good を維持する
   * （フェイルフリーズ。§7.1）。運営から見て最重要のセキュリティ事象なので、
   * 独立した種別にする。
   *
   * 2026-08-23 まで `relay_attempt`（オープンリレー試行）で通報していた。
   * 種別が違うと運営コンソールの分類・アラート・監査検索が全部誤った箱に
   * 入るうえ、「ただ更新が止まっているノード」と区別が付かない。
   */
  'signature_invalid',
] as const;
export type AgentEventKind = (typeof AGENT_EVENT_KIND)[number];
export const agentEventKindSchema = z.enum(AGENT_EVENT_KIND);

export const agentEventSchema = z.object({
  occurredAt: z.string().datetime(),
  kind: agentEventKindSchema,
  severity: z.enum(['info', 'warning', 'critical']),
  sourceIp: z.string().nullable().default(null),
  /** 該当があればメールボックス（乗っ取り検知）。 */
  mailboxId: z.string().nullable().default(null),
  domainId: z.string().nullable().default(null),
  count: z.number().int().positive().default(1),
  detail: z.string().max(2000),
  /** エージェントがローカルで遮断済みか。 */
  blockedLocally: z.boolean().default(false),
});
export type AgentEvent = z.infer<typeof agentEventSchema>;

/** `Idempotency-Key` 必須。ネットワーク再送で二重計上しないため。 */
export const agentEventsRequestSchema = z.object({
  events: z.array(agentEventSchema).min(1).max(500),
});
export type AgentEventsRequest = z.infer<typeof agentEventsRequestSchema>;

/* --------------------------------------------------------------- usage */

/** shim の `quotaget` を 30 分ごと + Postfix ログ 5 分集計（§3.5）。`Idempotency-Key` 必須。 */
export const agentUsageRequestSchema = z.object({
  collectedAt: z.string().datetime(),
  /** 集計期間の開始。CP はこの窓で重複排除する。 */
  windowStart: z.string().datetime(),
  windowEnd: z.string().datetime(),
  mailboxes: z
    .array(
      z.object({
        mailboxId: z.string().min(1),
        usedBytes: z.number().int().nonnegative(),
        messageCount: z.number().int().nonnegative(),
        inboundCount: z.number().int().nonnegative().default(0),
        outboundCount: z.number().int().nonnegative().default(0),
        /**
         * 送信バイト数（Postfix ログの `size=`）。R17。
         *
         * `usage_metric_key` には以前から `outbound_bytes` が居たのに
         * **生成経路が 1 つも無かった**。従量課金の単位を「通数」から
         * 「バイト」へ寄せる余地を残すために計測だけは常に取る。
         * 旧エージェントは送ってこないので既定 0。
         */
        outboundBytes: z.number().int().nonnegative().default(0),
        lastLoginAt: z.string().datetime().nullable().default(null),
      }),
    )
    .default([]),
  node: z.object({
    diskUsedBytes: z.number().int().nonnegative(),
    diskTotalBytes: z.number().int().nonnegative(),
    inboundCount: z.number().int().nonnegative(),
    outboundCount: z.number().int().nonnegative(),
    /** ノード全体の送信バイト数。旧エージェント互換のため既定 0。 */
    outboundBytes: z.number().int().nonnegative().default(0),
    rejectedCount: z.number().int().nonnegative(),
    spamCount: z.number().int().nonnegative(),
    virusCount: z.number().int().nonnegative(),
  }),
});
export type AgentUsageRequest = z.infer<typeof agentUsageRequestSchema>;

/* ----------------------------------------------------- 配送イベント（R0 §2.3） */

/**
 * 1 通 1 宛先の配送結果。**`usage` とは別の口にしてある。**
 *
 * `usage` は請求のための「量」で、遅れて届いても金額が合えばよい。
 * こちらは乗っ取り検知と抑止リストのための「事象」で、
 * 遅れると**その間ずっと存在しない宛先へ送り続ける**。
 * 冪等キーの粒度も、保持期間も、失敗時の扱いも違うので混ぜない。
 *
 * **本文は絶対に載せない。** 載せてよいのは宛先・コード・相手の応答文だけ
 * （`docs/CMS_DESIGN.md` §7 / CLAUDE.md §7-9）。
 */
export const AGENT_DELIVERY_DISPOSITION = [
  'delivered',
  'bounced',
  'deferred',
  /** 自ノードが送信を拒否した（レート超過・送信者不一致・アウトバウンド spam 判定）。 */
  'rejected',
] as const;
export type AgentDeliveryDisposition = (typeof AGENT_DELIVERY_DISPOSITION)[number];
export const agentDeliveryDispositionSchema = z.enum(AGENT_DELIVERY_DISPOSITION);

export const agentDeliveryEventSchema = z.object({
  occurredAt: z.string().datetime(),
  /**
   * 送信元メールボックス。**CP はこの ID が自ノードのものかを必ず検証する。**
   * 一斉配信のようにメールボックスを持たない送信は null。
   */
  mailboxId: z.string().min(1).nullable().default(null),
  /** メールボックスが無いときの組織の手掛かり（送信元ドメイン）。 */
  domainId: z.string().min(1).nullable().default(null),
  /** 宛先アドレス。抑止リストのキーになる。 */
  recipient: z.string().min(3).max(320),
  disposition: agentDeliveryDispositionSchema,
  /** `5.1.1` のような拡張ステータス。 */
  dsnStatus: z.string().max(32).nullable().default(null),
  /** `550` のような基本応答コード。 */
  smtpCode: z.number().int().min(100).max(599).nullable().default(null),
  /** 相手サーバーの応答文。**本文の断片を入れない**（先頭 512 文字まで）。 */
  diagnostic: z.string().max(512).nullable().default(null),
  /** 一斉配信の紐付け（あれば）。 */
  campaignId: z.string().min(1).nullable().default(null),
  /**
   * VERP の envelope-from（`bounces+<token>@…`）から取り出した受信者トークン。
   *
   * **`campaignId` をノードに推測させない**ための欄。ノードは契約者の
   * 宛先表を持たないので、「どのキャンペーンの誰か」を解けるのは CP だけである。
   * CP は `campaign_recipients.recipient_token` の一意索引
   * （`(campaign_id, recipient_token)`）を引いて紐付ける。
   * トークンは HMAC 12B でアドレスを復元できないため、
   * ログ経由でここに載っても名簿にはならない。
   */
  recipientToken: z.string().min(8).max(64).nullable().default(null),
});
export type AgentDeliveryEvent = z.infer<typeof agentDeliveryEventSchema>;

/** `Idempotency-Key` 必須。再送で二重にカウントすると乗っ取り検知が誤発火する。 */
export const agentDeliveryEventsRequestSchema = z.object({
  windowStart: z.string().datetime(),
  windowEnd: z.string().datetime(),
  events: z.array(agentDeliveryEventSchema).min(1).max(1000),
});
export type AgentDeliveryEventsRequest = z.infer<typeof agentDeliveryEventsRequestSchema>;

export const agentDeliveryEventsResponseSchema = z.object({
  accepted: z.number().int().nonnegative(),
  /** 自ノードのメールボックスでない等で捨てた件数。0 でないなら設定ずれ。 */
  ignored: z.number().int().nonnegative(),
  /** この報告で新たに抑止リストへ入った宛先の数。 */
  suppressed: z.number().int().nonnegative(),
  deduplicated: z.boolean().default(false),
});
export type AgentDeliveryEventsResponse = z.infer<typeof agentDeliveryEventsResponseSchema>;

/* ---------------------------------------------- 認証イベント（R134） */

/** 認証が起きた経路。DB 側は `node_auth_events.service` の CHECK と一致させる。 */
export const AGENT_AUTH_SERVICE = ['imap', 'pop3', 'smtp', 'submission', 'managesieve'] as const;
export type AgentAuthService = (typeof AGENT_AUTH_SERVICE)[number];
export const agentAuthServiceSchema = z.enum(AGENT_AUTH_SERVICE);

export const AGENT_AUTH_RESULT = ['success', 'failure'] as const;
export type AgentAuthResult = (typeof AGENT_AUTH_RESULT)[number];
export const agentAuthResultSchema = z.enum(AGENT_AUTH_RESULT);

/**
 * 認証イベント 1 件。**1 分バケットに畳んだあと**の形（R134）。
 *
 * 畳むのはノード側の仕事。IMAP IDLE のクライアントは 5〜30 分ごとに
 * 再認証するので、1 メールボックス 1 台でも 1 日 300 件になる。生のまま
 * 送ると 1000 メールボックスのノードで 1 日 30 万行が CP に積まれ、
 * **契約者が見たい「いつもと違うログイン」が量に埋もれる**。
 *
 * **認証に使われた文字列（パスワード・トークン）は一切運ばない。**
 * 運ぶのは利用者名・接続元 IP・結果・件数だけ。
 */
export const agentAuthEventSchema = z.object({
  /** 分単位に切り捨てた時刻。同じ組をこの粒度で 1 行に畳む。 */
  bucketAt: z.string().datetime(),
  service: agentAuthServiceSchema,
  result: agentAuthResultSchema,
  /**
   * ノードが観測した利用者名。**未知のアドレスでも送る**
   * （総当たりの観測がいちばん要る情報で、CP 側が捨てるかどうかを決める）。
   */
  username: z.string().min(1).max(320),
  clientIp: z.string().max(64).nullable().default(null),
  count: z.number().int().positive().max(1_000_000).default(1),
});
export type AgentAuthEvent = z.infer<typeof agentAuthEventSchema>;

/** `Idempotency-Key` 必須。再送で件数が二重に積まれないよう CP 側も一意索引で守る。 */
export const agentAuthEventsRequestSchema = z.object({
  windowStart: z.string().datetime(),
  windowEnd: z.string().datetime(),
  events: z.array(agentAuthEventSchema).min(1).max(1000),
});
export type AgentAuthEventsRequest = z.infer<typeof agentAuthEventsRequestSchema>;

export const agentAuthEventsResponseSchema = z.object({
  accepted: z.number().int().nonnegative(),
  /** 自ノードのメールボックスに解決できず、組織を決められなかった件数。 */
  ignored: z.number().int().nonnegative(),
  deduplicated: z.boolean().default(false),
});
export type AgentAuthEventsResponse = z.infer<typeof agentAuthEventsResponseSchema>;

/* -------------------------------------------------------- FBL（ARF 苦情） */

/**
 * ノードが受け取った ARF（RFC 5965）から**抽出済みの項目だけ**を運ぶ（A283 / A303）。
 *
 * **ARF の原文は運ばない。** 原文には元メールのヘッダと本文が丸ごと入るので、
 * CP へ運べば経路とログに本文が乗る（CLAUDE.md §7-9 に反する）。
 * ノード側で `parseArfReport()` を通し、ここに残った数項目だけを送る。
 *
 * `feedbackType` を**そのまま文字列で運ぶ**のは意図的で、分類（苦情か、
 * 苦情の撤回か、運営が見るだけか）は CP 側が `classifyArfFeedback()` で
 * やり直す。ノードの実装を直しても古い版のエージェントは残るので、
 * `not-spam`（苦情の撤回）を苦情として送ってくる版が 1 つでもあると
 * 苦情率が跳ね上がって正常な配信が緊急停止する。
 */
export const agentFeedbackReportSchema = z.object({
  feedbackType: z.string().max(64),
  originalRcptTo: z.string().max(320).nullable().default(null),
  originalMailFrom: z.string().max(320).nullable().default(null),
  arrivalDate: z.string().datetime().nullable().default(null),
  reportedDomain: z.string().max(253).nullable().default(null),
  sourceIp: z.string().max(64).nullable().default(null),
  /** ノードが FBL メールを受け取った時刻。`arrivalDate` が読めないときの代替。 */
  receivedAt: z.string().datetime(),
});
export type AgentFeedbackReport = z.infer<typeof agentFeedbackReportSchema>;

/** 1 リクエストの上限。CP 側 `FEEDBACK_REPORT_BATCH_MAX` と揃える。 */
export const AGENT_FEEDBACK_REPORT_BATCH_MAX = 500;

/** `Idempotency-Key` 必須。再送で二重に数えると苦情率が実際の倍になる。 */
export const agentFeedbackReportsRequestSchema = z.object({
  reports: z.array(agentFeedbackReportSchema).min(1).max(AGENT_FEEDBACK_REPORT_BATCH_MAX),
});
export type AgentFeedbackReportsRequest = z.infer<typeof agentFeedbackReportsRequestSchema>;

export const agentFeedbackReportsResponseSchema = z.object({
  received: z.number().int().nonnegative(),
  complaints: z.number().int().nonnegative(),
  notSpam: z.number().int().nonnegative(),
  review: z.number().int().nonnegative(),
  unmatched: z.number().int().nonnegative(),
  deduplicated: z.boolean().default(false),
});
export type AgentFeedbackReportsResponse = z.infer<typeof agentFeedbackReportsResponseSchema>;

/* --------------------------------------------------------------- audit */

/**
 * ノード側の `agent_changes`（ハッシュチェーン + Ed25519 署名）を CP へ出荷する。
 * CP DB が改竄されてもノード側に原本が残る（§7.8）。`Idempotency-Key` 必須。
 */
export const agentAuditEntrySchema = z.object({
  id: z.string().min(1),
  at: z.string().datetime(),
  generation: z.number().int().nonnegative(),
  kind: z.string().min(1),
  target: z.string(),
  summary: z.string().max(2000),
  detail: z.record(z.string(), z.unknown()).default({}),
  prevHash: z.string().nullable(),
  hash: sha256HexSchema,
  /** ノードの Ed25519 秘密鍵による署名（base64）。 */
  signature: z.string().min(1),
});
export type AgentAuditEntry = z.infer<typeof agentAuditEntrySchema>;

export const agentAuditRequestSchema = z.object({
  entries: z.array(agentAuditEntrySchema).min(1).max(500),
});
export type AgentAuditRequest = z.infer<typeof agentAuditRequestSchema>;

/* -------------------------------------------------------- backup-status */

export const agentBackupStatusRequestSchema = z.object({
  ranAt: z.string().datetime(),
  ok: z.boolean(),
  /** restic のスナップショット ID。 */
  snapshotId: z.string().nullable().default(null),
  bytesAdded: z.number().int().nonnegative().default(0),
  durationSec: z.number().int().nonnegative().default(0),
  /** 直近の復元テストの実施時刻。運用上ここが古いこと自体がアラート対象。 */
  lastRestoreCheckAt: z.string().datetime().nullable().default(null),
  message: z.string().max(2000).nullable().default(null),
});
export type AgentBackupStatusRequest = z.infer<typeof agentBackupStatusRequestSchema>;

/* -------------------------------------------------------- release/report */

export const AGENT_RELEASE_PHASE = [
  'started',
  'pulled',
  'applied',
  'soaking',
  'succeeded',
  'failed',
  'rolled_back',
] as const;
export type AgentReleasePhase = (typeof AGENT_RELEASE_PHASE)[number];
export const agentReleasePhaseSchema = z.enum(AGENT_RELEASE_PHASE);

export const agentReleaseReportRequestSchema = z.object({
  rolloutId: z.string().min(1),
  version: z.string().min(1),
  phase: agentReleasePhaseSchema,
  /** soak 判定（§8.4）に使う実測値。 */
  health: z
    .object({
      smtpOk: z.boolean(),
      imapOk: z.boolean(),
      lmtpOk: z.boolean(),
      deferredDelta: z.number().int(),
      errorRate: z.number(),
    })
    .partial()
    .default({}),
  message: z.string().max(4000).nullable().default(null),
  reportedAt: z.string().datetime(),
});
export type AgentReleaseReportRequest = z.infer<typeof agentReleaseReportRequestSchema>;

/* ------------------------------------------------------------- commands */

export const agentCommandPollRequestSchema = z.object({
  /** long-poll の待機秒数。 */
  wait: z.number().int().min(0).max(AGENT_LONGPOLL_DEFAULT_SEC).default(AGENT_LONGPOLL_DEFAULT_SEC),
  /** 一度に受け取る最大件数。 */
  max: z.number().int().min(1).max(20).default(5),
});
export type AgentCommandPollRequest = z.infer<typeof agentCommandPollRequestSchema>;

export const agentCommandPollResponseSchema = z.object({
  commands: z.array(nodeCommandSchema).default([]),
});
export type AgentCommandPollResponse = z.infer<typeof agentCommandPollResponseSchema>;

export const agentCommandResultRequestSchema = nodeCommandResultSchema;
export type AgentCommandResultRequest = z.infer<typeof agentCommandResultRequestSchema>;

/* ----------------------------------------------------- ACME DNS チャレンジ */

/**
 * `certMode='agent'` のときにエージェントが CP へ TXT レコードの設置を依頼する。
 * ノードは顧客の DNS を触れないため、`_acme-challenge` の設置は必ず CP 側で行う。
 */
export const agentAcmeDnsChallengeRequestSchema = z.object({
  certId: z.string().min(1),
  domain: z.string().min(1),
  /** `_acme-challenge.<domain>` に置く TXT 値。 */
  txtValue: z.string().min(1),
  /** 設置か撤去か。撤去を忘れると次回の検証が古い値で通ってしまう。 */
  action: z.enum(['upsert', 'remove']),
});
export type AgentAcmeDnsChallengeRequest = z.infer<typeof agentAcmeDnsChallengeRequestSchema>;

export const agentAcmeDnsChallengeResponseSchema = z.object({
  accepted: z.boolean(),
  /** CP が実際に反映を確認できるまでの推奨待機秒数。 */
  propagationWaitSec: z.number().int().nonnegative(),
});
export type AgentAcmeDnsChallengeResponse = z.infer<typeof agentAcmeDnsChallengeResponseSchema>;

/* ============================================================================ */
/* 一斉配信（bulk）                                                             */
/* ============================================================================ */

/**
 * `POST /agent/bulk/lease` … 注入するバッチを取りに行く。
 *
 * 設計上の要点（`docs/CMS_DESIGN.md` §5.6 の LEASE より右側）:
 *
 * ① **ノードが取りに行く。** CP から押し込むと、ノードが落ちている間の
 *    行方が CP の再送キューに依存し、二重注入の窓が開く。
 * ② **リースには期限がある。** ノードが落ちても `lease_expires_at` が
 *    過ぎれば別ノードが取り直せる（バッチが 1 つ死ぬと配信全体が
 *    永久に完了しない、という状態を作らない）。
 * ③ **払い出しの直前にもう一度サプレッションを見る。** 展開時（`campaign.expand`）
 *    に見たあと、解禁〜注入の間に解除した人へ送らないため
 *    （特定電子メール法 3 条 3 項）。
 * ④ **本文は載せない。** `artifactId` と sha256 だけを返し、実体は
 *    `GET /agent/bulk/artifacts/:id` で 1 回だけ取る。
 */
export const agentBulkLeaseRequestSchema = z.object({
  /** 一度にリースするバッチ数。ノードの空き具合で決める。 */
  maxBatches: z.number().int().min(1).max(10).default(2),
  /** 受け取る宛先の総数の上限（バッチ単位の切りの良いところで止める）。 */
  maxRecipients: z.number().int().min(1).max(5000).default(2000),
  /** リースの希望秒数。CP 側の上限で丸められる。 */
  leaseSeconds: z.number().int().min(60).max(3600).default(900),
});
export type AgentBulkLeaseRequest = z.infer<typeof agentBulkLeaseRequestSchema>;

/**
 * 1 宛先。**差込値以外の個人情報は載らない**（`mergeVars` は契約者が
 * 差込に使うと決めた値だけ）。`token` は VERP と One-Click 解除に使う。
 */
export const agentBulkRecipientSchema = z.object({
  recipientId: z.string().min(1),
  address: z.string().min(3).max(320),
  /** `campaign_recipients.recipient_token`（HMAC 12B の base64url）。 */
  token: z.string().min(8).max(64),
  mergeVars: z.record(z.string(), z.union([z.string(), z.number(), z.null()])).default({}),
});
export type AgentBulkRecipient = z.infer<typeof agentBulkRecipientSchema>;

/**
 * 1 バッチ。エンベロープの組み立てに要る値をここに閉じ込める。
 *
 * **ノードが CP の DB を推測しないで済むこと**を優先している。
 * 例えば `unsubscribeBaseUrl` を配るのは、ノードが公開面の URL を
 * env から組み立てると、CP が独自ドメインへ移った瞬間に
 * 「解除リンクだけ古いホストを指す」が黙って起きるため。
 */
export const agentBulkBatchSchema = z.object({
  batchId: z.string().min(1),
  campaignId: z.string().min(1),
  organizationId: z.string().min(1),
  seq: z.number().int().nonnegative(),
  /** ビルド済み成果物。本文は別 GET で取る。 */
  artifactId: z.string().min(1),
  artifactSha256: sha256HexSchema,
  subject: z.string().max(998),
  fromAddress: z.string().min(3).max(320),
  fromDisplayName: z.string().max(200).nullable().default(null),
  replyTo: z.string().max(320).nullable().default(null),
  /** VERP の受け口。`<bounceLocalPart>+<token>@<bounceDomain>` を envelope-from にする。 */
  bounceLocalPart: z.string().min(1).max(64),
  bounceDomain: z.string().min(1).max(253),
  /** DKIM 署名に使うドメイン（rspamd の from-ACL と一致していること）。 */
  signingDomain: z.string().min(1).max(253),
  /**
   * 宛先リスト（`contact_lists.id`）。**`Feedback-ID` / `List-ID` の
   * 組み立てにしか使わない。**
   *
   * Gmail は個別の ARF を返さないので、`fbl-ingest` 経路では Gmail 宛の
   * 苦情が 1 件も観測できない。Google Postmaster Tools が集計する単位が
   * `Feedback-ID` の各フィールドなので、これを出しておかないと
   * 「キャンペーン別の苦情率」を後から知る手段が無くなる。
   */
  listId: z.string().max(64).nullable().default(null),
  /** `{{unsubscribe_url}}` と `List-Unsubscribe` の基底 URL（公開面のオリジン）。 */
  publicBaseUrl: z.string().url(),
  /** キャンペーン名など、差込カタログの文脈値。**宛先ごとの値は含まない**。 */
  contextVars: z.record(z.string(), z.string()).default({}),
  recipients: z.array(agentBulkRecipientSchema).max(5000).default([]),
});
export type AgentBulkBatch = z.infer<typeof agentBulkBatchSchema>;

export const agentBulkLeaseResponseSchema = z.object({
  batches: z.array(agentBulkBatchSchema).default([]),
  /** 実際に与えられたリース期限。 */
  leaseExpiresAt: z.string().datetime().nullable().default(null),
  /**
   * 次に取りに来るまでの推奨待機秒数。
   * 0 件のときに CP が「まだ無い」と伝える唯一の手段で、
   * これが無いとノードが 1 秒間隔で叩き続ける。
   */
  retryAfterSec: z.number().int().min(1).max(3600).default(30),
});
export type AgentBulkLeaseResponse = z.infer<typeof agentBulkLeaseResponseSchema>;

/** 注入の結果（1 宛先）。**SMTP 応答の全文は載せない**（本文の断片が混ざる）。 */
export const agentBulkResultSchema = z.object({
  recipientId: z.string().min(1),
  outcome: z.enum(['injected', 'failed', 'skipped']),
  /** 注入時に採番した Message-ID。DSN との突合に使う。 */
  messageId: z.string().max(320).nullable().default(null),
  smtpCode: z.number().int().min(100).max(599).nullable().default(null),
  /** 分類済みの理由 symbol（`suppressed` / `smtp_reject` など）。自由文は入れない。 */
  reason: z.string().max(64).nullable().default(null),
});
export type AgentBulkResult = z.infer<typeof agentBulkResultSchema>;

/**
 * `POST /agent/bulk/report` … リースしたバッチの結果を返す。
 *
 * **`Idempotency-Key` 必須。** 同じ報告の再送で `campaign_recipients` の
 * 状態を巻き戻さないため（`sent` を `pending` に戻すと再注入される）。
 */
export const agentBulkReportRequestSchema = z.object({
  batchId: z.string().min(1),
  campaignId: z.string().min(1),
  /** バッチを流し切ったか。false なら CP はリースを維持する（部分報告）。 */
  finished: z.boolean().default(true),
  results: z.array(agentBulkResultSchema).min(1).max(5000),
});
export type AgentBulkReportRequest = z.infer<typeof agentBulkReportRequestSchema>;

export const agentBulkReportResponseSchema = z.object({
  applied: z.number().int().nonnegative(),
  /** 自ノードのリースでない等で捨てた件数。0 でないならリースがずれている。 */
  ignored: z.number().int().nonnegative(),
  batchState: z.string().max(32),
});
export type AgentBulkReportResponse = z.infer<typeof agentBulkReportResponseSchema>;

/** `GET /agent/bulk/artifacts/:id` の応答。**この 1 本だけが本文を運ぶ。** */
export const agentBulkArtifactResponseSchema = z.object({
  artifactId: z.string().min(1),
  sha256: sha256HexSchema,
  html: z.string(),
  text: z.string(),
});
export type AgentBulkArtifactResponse = z.infer<typeof agentBulkArtifactResponseSchema>;

/* ------------------------------------------------------------ 抑止リスト */

/**
 * `GET /agent/suppressions?since=…` … 抑止リストの差分。
 *
 * **ノード側で強制するために配る。** CP 側だけで持っていると、
 * 一斉配信以外（契約者自身の submission・アプリからの送信）で
 * 恒久バウンス済みのアドレスへ何度でも送れてしまい、
 * 共有 IP の評判が落ちるのを止められない。
 *
 * 組織 ID を必ず伴うのは、抑止が**組織単位のスコープ**だから。
 * アドレスだけで持つと、A 社で解除した人が B 社の正規の宛先でも
 * 拒否されるという越境が起きる。
 */
export const agentSuppressionEntrySchema = z.object({
  organizationId: z.string().min(1),
  address: z.string().min(3).max(320),
  reason: z.string().max(32),
  /** 期限付き抑止（ソフトバウンス）は時刻が入る。恒久は null。 */
  expiresAt: z.string().datetime().nullable().default(null),
  /** 解除された行は `released: true` で配る（ノード側で削除する）。 */
  released: z.boolean().default(false),
  updatedAt: z.string().datetime(),
});
export type AgentSuppressionEntry = z.infer<typeof agentSuppressionEntrySchema>;

export const agentSuppressionsQuerySchema = z.object({
  /** 前回取り切った時刻。省略時は全件（初回同期）。 */
  since: z.string().datetime().optional(),
  limit: z.number().int().min(1).max(5000).default(2000),
});
export type AgentSuppressionsQuery = z.infer<typeof agentSuppressionsQuerySchema>;

export const agentSuppressionsResponseSchema = z.object({
  entries: z.array(agentSuppressionEntrySchema).default([]),
  /** 次回の `since`。**返された最後の行の `updated_at`** で、now() ではない。 */
  cursor: z.string().datetime().nullable().default(null),
  /** まだ続きがあるか。true ならノードは間を置かず次を取りに来る。 */
  hasMore: z.boolean().default(false),
});
export type AgentSuppressionsResponse = z.infer<typeof agentSuppressionsResponseSchema>;
